// Track 5.1 — Yaw sweep estimation.
//
// A guided 10-second protocol that asks the rider to look 20° left, then
// 20° right, while staying in their current riding position. We sample
// (yaw, CdA) at ~6 Hz across the sweep and fit a quadratic CdA(yaw) curve.
//
// Why this matters: real-road CdA is yaw-averaged. Wind isn't always head-on;
// typical road conditions average ~10° apparent wind angle. A rider whose
// frontal CdA is 0.241 but whose yaw-averaged CdA is 0.258 will under-perform
// their indoor number in the field. Fitting CdA(yaw) lets the session report
// quote a realistic "predicted 40km TT gain" instead of a fantasy.
//
// Mechanics:
//   - Detect head yaw from MediaPipe face landmarks (eye-line vs nose direction).
//   - Run a state machine: WAITING → LOOK_LEFT → CENTER → LOOK_RIGHT → DONE.
//   - Each phase has a target yaw, a tolerance, and a 2.5s hold requirement.
//   - On DONE: fit a parabola CdA(yaw) = a*yaw² + b*yaw + c via least squares.
//   - Return curve + the 10°-wind-averaged CdA (the number to quote).

export type YawSweepPhase =
  | 'idle'
  | 'waiting'      // baseline locked, awaiting "start sweep" trigger
  | 'look_left'
  | 'return_left'
  | 'look_right'
  | 'return_right'
  // Wave 13 / Track A — body-rotation phases run AFTER the head sweep.
  // The rider rotates their shoulders/torso ±15° so we can capture full-
  // body silhouettes from three viewing angles for whole-body voxel
  // carving in visualHull.ts. Adds ~8 s to the protocol.
  | 'body_left'
  | 'body_return_left'
  | 'body_right'
  | 'body_return_right'
  | 'fitting'
  | 'done'
  | 'failed';

export interface YawSweepResult {
  /** Parabola coefficients CdA(yaw_deg) = a·yaw² + b·yaw + c */
  a: number;
  b: number;
  c: number;
  /** Yaw-averaged CdA at the typical real-road 10° wind condition. */
  cda_at_10deg_yaw: number;
  /** Raw samples that fed the fit, exposed for the report's chart. */
  samples: Array<{ yaw_deg: number; cda: number }>;
  /** Wave 12 — Multi-view keyframes captured at look_left / center /
   *  look_right. Used by visualHull.ts to reconstruct the rider's 3D
   *  head shape from silhouette intersection. Empty if no captures
   *  were submitted during the sweep. */
  keyframes: YawSweepKeyframe[];
  /** Wave 13 / Track A — body-rotation keyframes captured at
   *  body_left / center / body_right. Used by buildVisualHullBody to
   *  carve the rider's torso/arms/legs. Empty if the body sweep was
   *  skipped or failed to detect rotation. */
  bodyKeyframes: YawSweepKeyframe[];
}

export interface YawSweepKeyframe {
  /** Yaw angle in degrees when the frame was captured. Negative = left. */
  yawDeg: number;
  /** Cloned segmentation label map at capture time (0 = bg, 1=head,
   *  2=torso, 3=arms, 4=legs). Same dimensions as the mask used during
   *  the sweep. */
  labelMap: Uint8Array;
  maskW: number;
  maskH: number;
  /** Approximate calibration scale at capture time, used to translate
   *  voxel positions in metres into image pixels during the hull carving
   *  visibility test. */
  pxPerM: number;
  /** Image-space bbox of the head region (label==1), in pixels.
   *  [x, y, w, h]. */
  headBboxPx: [number, number, number, number] | null;
  /** Wave 13 / Track A — Image-space bbox of the *whole rider* region
   *  (any non-bg label), in pixels. Used by buildVisualHullBody to
   *  anchor the body-voxel grid in world coords. Only populated on
   *  body keyframes. */
  bodyBboxPx?: [number, number, number, number] | null;
}

const PHASE_TARGETS: Record<Exclude<YawSweepPhase, 'idle' | 'waiting' | 'fitting' | 'done' | 'failed'>, { yaw_deg: number; tol_deg: number; hold_ms: number; label: string; axis: 'head' | 'body' }> = {
  look_left:           { yaw_deg: -20, tol_deg: 6, hold_ms: 2_000, label: 'Look 20° left',         axis: 'head' },
  return_left:         { yaw_deg: 0,   tol_deg: 5, hold_ms: 800,   label: 'Back to center',        axis: 'head' },
  look_right:          { yaw_deg: 20,  tol_deg: 6, hold_ms: 2_000, label: 'Look 20° right',        axis: 'head' },
  return_right:        { yaw_deg: 0,   tol_deg: 5, hold_ms: 800,   label: 'Back to center',        axis: 'head' },
  // Body rotation — wider tolerance because shoulder-foreshortening is
  // noisier than face-landmark yaw, slightly smaller angle target because
  // most riders can't comfortably rotate their bike-locked torso > 15°.
  body_left:           { yaw_deg: -15, tol_deg: 8, hold_ms: 1_800, label: 'Turn shoulders left',   axis: 'body' },
  body_return_left:    { yaw_deg: 0,   tol_deg: 7, hold_ms: 600,   label: 'Shoulders square',      axis: 'body' },
  body_right:          { yaw_deg: 15,  tol_deg: 8, hold_ms: 1_800, label: 'Turn shoulders right',  axis: 'body' },
  body_return_right:   { yaw_deg: 0,   tol_deg: 7, hold_ms: 600,   label: 'Shoulders square',      axis: 'body' },
};

const PHASE_ORDER: Array<keyof typeof PHASE_TARGETS> = [
  'look_left',
  'return_left',
  'look_right',
  'return_right',
  'body_left',
  'body_return_left',
  'body_right',
  'body_return_right',
];

export class YawSweep {
  private phase: YawSweepPhase = 'idle';
  private phaseIdx = 0;
  private holdStart: number | null = null;
  private samples: Array<{ yaw_deg: number; cda: number }> = [];
  private keyframes: YawSweepKeyframe[] = [];
  // Wave 13 / Track A — body keyframes are captured in the body-rotation
  // phases and stored separately so the head-hull carver doesn't accept
  // them by mistake.
  private bodyKeyframes: YawSweepKeyframe[] = [];
  // Phase that the next captureKeyframe call should be attributed to.
  // We capture once per look_left / one center frame between / once per
  // look_right hold, so this stays "ready" for one frame and then resets.
  private capturePending: 'look_left' | 'center' | 'look_right' | null = null;
  private bodyCapturePending: 'body_left' | 'body_center' | 'body_right' | null = null;
  private result: YawSweepResult | null = null;

  start(): void {
    this.phase = 'look_left';
    this.phaseIdx = 0;
    this.holdStart = null;
    this.samples = [];
    this.keyframes = [];
    this.bodyKeyframes = [];
    this.capturePending = null;
    this.bodyCapturePending = null;
    this.result = null;
  }

  /** Wave 14+ / Rapha Fit — start the body-rotation half of the sweep
   *  only, skipping the four head phases. The yaw-response fit at the
   *  end will fail gracefully (no head samples) but we don't need it
   *  for fit; we only consume `bodyKeyframes`. */
  startBodyOnly(): void {
    this.phase = 'body_left';
    // PHASE_ORDER has the head phases at 0..3 and body phases at 4..7;
    // jumping the index to 4 skips straight to body_left.
    this.phaseIdx = 4;
    this.holdStart = null;
    this.samples = [];
    this.keyframes = [];
    this.bodyKeyframes = [];
    this.capturePending = null;
    this.bodyCapturePending = null;
    this.result = null;
  }

  abort(): void {
    this.phase = 'failed';
    this.holdStart = null;
  }

  currentPhase(): YawSweepPhase { return this.phase; }
  currentPrompt(): string | null {
    if (this.phase === 'idle' || this.phase === 'waiting') return null;
    if (this.phase === 'done' || this.phase === 'failed') return null;
    if (this.phase === 'fitting') return 'Computing yaw response…';
    return PHASE_TARGETS[this.phase as keyof typeof PHASE_TARGETS]?.label ?? null;
  }

  result_(): YawSweepResult | null { return this.result; }

  /** Feed each frame. Returns true while the sweep is in progress (caller
   *  uses this to keep the prompt visible).
   *  Wave 13 / Track A — `bodyYawDeg` is consulted instead of `headYawDeg`
   *  during the body-rotation phases. */
  update(input: {
    now: number;
    headYawDeg: number | null;
    bodyYawDeg?: number | null;
    cda: number | null;
  }): boolean {
    if (this.phase === 'idle' || this.phase === 'waiting' || this.phase === 'done' || this.phase === 'failed') {
      return false;
    }
    if (this.phase === 'fitting') return true;

    const target = PHASE_TARGETS[this.phase as keyof typeof PHASE_TARGETS];
    if (!target) return false;

    // The active angle is head yaw for the head sweep, body yaw for the
    // body sweep. Body phases tolerate a missing measurement (skip the
    // phase rather than stall the protocol) — see below.
    const yaw = target.axis === 'body' ? input.bodyYawDeg ?? null : input.headYawDeg;
    const inBand =
      yaw != null && Math.abs(yaw - target.yaw_deg) <= target.tol_deg;

    if (inBand) {
      if (this.holdStart == null) this.holdStart = input.now;
      // Collect samples during the "look_*" phases (not during returns).
      if (
        (this.phase === 'look_left' || this.phase === 'look_right') &&
        input.cda != null &&
        yaw != null
      ) {
        this.samples.push({ yaw_deg: yaw, cda: input.cda });
      }
      // Wave 12 — request a keyframe capture mid-hold. We pick the mid
      // point of each hold (when the rider is most stable) and emit the
      // capture-pending flag for one frame; the pipeline tick reads it
      // and supplies the silhouette snapshot.
      const heldFor = input.now - this.holdStart;
      const halfHold = target.hold_ms * 0.5;
      if (
        this.phase === 'look_left' &&
        heldFor >= halfHold &&
        !this.keyframes.some((k) => k.yawDeg < -5)
      ) {
        this.capturePending = 'look_left';
      } else if (
        this.phase === 'return_left' &&
        heldFor >= 200 && // a brief settle at center
        !this.keyframes.some((k) => Math.abs(k.yawDeg) <= 5)
      ) {
        this.capturePending = 'center';
      } else if (
        this.phase === 'look_right' &&
        heldFor >= halfHold &&
        !this.keyframes.some((k) => k.yawDeg > 5)
      ) {
        this.capturePending = 'look_right';
      } else if (
        // Wave 13 / Track A — body keyframes (separate bucket from head).
        this.phase === 'body_left' &&
        heldFor >= halfHold &&
        !this.bodyKeyframes.some((k) => k.yawDeg < -5)
      ) {
        this.bodyCapturePending = 'body_left';
      } else if (
        this.phase === 'body_return_left' &&
        heldFor >= 200 &&
        !this.bodyKeyframes.some((k) => Math.abs(k.yawDeg) <= 5)
      ) {
        this.bodyCapturePending = 'body_center';
      } else if (
        this.phase === 'body_right' &&
        heldFor >= halfHold &&
        !this.bodyKeyframes.some((k) => k.yawDeg > 5)
      ) {
        this.bodyCapturePending = 'body_right';
      }
      if (input.now - this.holdStart >= target.hold_ms) {
        this.phaseIdx += 1;
        if (this.phaseIdx >= PHASE_ORDER.length) {
          this.phase = 'fitting';
          this.fit();
          return true;
        }
        this.phase = PHASE_ORDER[this.phaseIdx];
        this.holdStart = null;
      }
    } else {
      // Out of band — restart the hold but don't drop existing samples.
      this.holdStart = null;
      // Body-yaw detection is noisier than head-yaw; if the body axis is
      // simply missing (no worldLandmarks) we don't want to stall here
      // forever. Skip past the body phases when bodyYawDeg has been null
      // for 4 s — the head sweep already finished, and a missing body
      // hull just falls back to silhouette extrusion.
      if (target.axis === 'body' && yaw == null) {
        this._bodyMissingSince ??= input.now;
        if (input.now - this._bodyMissingSince > 4_000) {
          // Skip directly to fitting; bodyKeyframes stays empty.
          this.phase = 'fitting';
          this.fit();
          return true;
        }
      } else if (target.axis === 'body') {
        this._bodyMissingSince = null;
      }
    }

    return true;
  }

  // Tracks how long bodyYaw has been null so we can give up on the body
  // sweep gracefully when the rider's worldLandmarks don't allow detection.
  private _bodyMissingSince: number | null = null;

  /** Wave 12 — is a keyframe capture due? When true, the pipeline should
   *  clone the current label map + yaw + calibration and submit it via
   *  acceptKeyframe(). One-shot per slot (look_left / center / look_right). */
  needsKeyframe(): 'look_left' | 'center' | 'look_right' | null {
    return this.capturePending;
  }

  /** Wave 13 / Track A — same but for the body-rotation phases. The
   *  pipeline submits a labelMap snapshot via acceptBodyKeyframe(). */
  needsBodyKeyframe(): 'body_left' | 'body_center' | 'body_right' | null {
    return this.bodyCapturePending;
  }

  /** Wave 12 — submit a captured keyframe. Caller is responsible for
   *  copying the label map (we don't clone here — efficient zero-copy
   *  caller pattern when the labelMap is already a fresh per-frame
   *  allocation, which it isn't in our pipeline; the pipeline must clone). */
  acceptKeyframe(kf: YawSweepKeyframe): void {
    this.keyframes.push(kf);
    this.capturePending = null;
  }

  /** Wave 13 / Track A — submit a body keyframe. Same clone-responsibility
   *  contract as acceptKeyframe. */
  acceptBodyKeyframe(kf: YawSweepKeyframe): void {
    this.bodyKeyframes.push(kf);
    this.bodyCapturePending = null;
  }

  /** All captured keyframes (length 0..3, one per hold phase). Exposed
   *  to the result on done. */
  capturedKeyframes(): YawSweepKeyframe[] {
    return this.keyframes;
  }

  /** Wave 13 / Track A — all body keyframes. */
  capturedBodyKeyframes(): YawSweepKeyframe[] {
    return this.bodyKeyframes;
  }

  /** Also accept center samples — caller feeds these *before* starting the
   *  sweep so we have at least one (yaw≈0, cda) anchor in the fit. */
  feedCenterSample(cda: number): void {
    this.samples.push({ yaw_deg: 0, cda });
    // Cap to avoid biasing the fit toward center.
    if (this.samples.length > 60) {
      const center = this.samples.filter((s) => Math.abs(s.yaw_deg) < 3);
      const wings = this.samples.filter((s) => Math.abs(s.yaw_deg) >= 3);
      this.samples = [...center.slice(-10), ...wings];
    }
  }

  private fit(): void {
    if (this.samples.length < 6) {
      this.phase = 'failed';
      return;
    }
    // Least-squares fit of y = a*x² + b*x + c.
    // Build normal equations: [Σx⁴ Σx³ Σx²][a]   [Σx²y]
    //                          [Σx³ Σx² Σx ][b] = [Σxy]
    //                          [Σx² Σx  n  ][c]   [Σy]
    let sx = 0, sx2 = 0, sx3 = 0, sx4 = 0;
    let sy = 0, sxy = 0, sx2y = 0;
    const n = this.samples.length;
    for (const { yaw_deg, cda } of this.samples) {
      const x = yaw_deg;
      const x2 = x * x;
      sx += x; sx2 += x2; sx3 += x2 * x; sx4 += x2 * x2;
      sy += cda; sxy += x * cda; sx2y += x2 * cda;
    }
    const sol = solve3x3([
      [sx4, sx3, sx2],
      [sx3, sx2, sx],
      [sx2, sx, n],
    ], [sx2y, sxy, sy]);
    if (!sol) {
      this.phase = 'failed';
      return;
    }
    const [a, b, c] = sol;

    // Real-road yaw rarely exceeds 10° unsigned. Use the average of CdA(+10)
    // and CdA(-10) since drag is symmetric across yaw direction.
    const cda_at_10 = (cda_at(a, b, c, 10) + cda_at(a, b, c, -10)) / 2;

    this.result = {
      a, b, c,
      cda_at_10deg_yaw: cda_at_10,
      samples: this.samples.slice(),
      keyframes: this.keyframes.slice(),
      bodyKeyframes: this.bodyKeyframes.slice(),
    };
    this.phase = 'done';
  }
}

function cda_at(a: number, b: number, c: number, yaw: number): number {
  return a * yaw * yaw + b * yaw + c;
}

/** Tiny 3×3 solver via Cramer's rule. Adequate for the well-conditioned
 *  yaw-sweep matrix — no need to pull in a linear-algebra library. */
function solve3x3(M: number[][], v: number[]): [number, number, number] | null {
  const det = det3(M);
  if (Math.abs(det) < 1e-12) return null;
  const m0 = clone3x3(M); m0[0][0] = v[0]; m0[1][0] = v[1]; m0[2][0] = v[2];
  const m1 = clone3x3(M); m1[0][1] = v[0]; m1[1][1] = v[1]; m1[2][1] = v[2];
  const m2 = clone3x3(M); m2[0][2] = v[0]; m2[1][2] = v[1]; m2[2][2] = v[2];
  return [det3(m0) / det, det3(m1) / det, det3(m2) / det];
}
function det3(m: number[][]): number {
  return (
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0])
  );
}
function clone3x3(m: number[][]): number[][] {
  return [m[0].slice(), m[1].slice(), m[2].slice()];
}

/** Wave 13 / Track A — Estimate body yaw in degrees from MediaPipe world
 *  landmarks. Uses two complementary signals:
 *   - foreshortening: 2D shoulder width shrinks as cos(yaw) when the rider
 *     rotates the shoulder line away from the camera plane.
 *   - sign from the world-Z depth difference between left vs right
 *     shoulder (the further shoulder has smaller z in the right-handed
 *     world frame).
 *  Returns null if shoulders aren't visible or the 2D/3D widths are
 *  degenerate. Tolerates ±35° before the cos() inversion saturates. */
export function estimateBodyYawDeg(
  landmarks2D: Array<{ x: number; y: number; visibility?: number }> | null,
  worldLandmarks: Array<{ x: number; y: number; z: number; visibility?: number }> | null,
): number | null {
  if (!landmarks2D || !worldLandmarks) return null;
  const lSh2 = landmarks2D[11];
  const rSh2 = landmarks2D[12];
  const lSh3 = worldLandmarks[11];
  const rSh3 = worldLandmarks[12];
  if (!lSh2 || !rSh2 || !lSh3 || !rSh3) return null;
  if ((lSh2.visibility ?? 0) < 0.5 || (rSh2.visibility ?? 0) < 0.5) return null;
  const w3 = Math.hypot(lSh3.x - rSh3.x, lSh3.y - rSh3.y, lSh3.z - rSh3.z);
  // 2D distance is in normalized [0,1] coords; convert via shoulder span
  // ratio to avoid hard-coded image-dimension assumptions. We compare the
  // 2D x-spread (the foreshortening-sensitive axis) to the full 3D length.
  const w2 = Math.abs(lSh2.x - rSh2.x);
  // Map 2D normalized x-span (≈0..0.5) into the same scale as the 3D
  // shoulder width by remembering the rider's typical square-on value.
  // Pragmatic baseline: when zSpread is ~0 (square-on), w2 ≈ w3 * px/m
  // scaler. We don't have px/m here, so use the running max of w2 we've
  // seen as the square-on reference.
  if (w3 < 0.1 || w2 < 1e-3) return null;
  // Cache the square-on reference statically — set the first time the
  // rider's 2D width exceeds 90% of the 3D width (i.e., near-frontal).
  if (w2 > _bodyYawRef.maxRatio * 0.95) {
    _bodyYawRef.maxRatio = w2 / w3;
  }
  const cosYaw = Math.min(1, (w2 / w3) / Math.max(_bodyYawRef.maxRatio, 1e-3));
  // Sign from z-depth: rotating right brings right shoulder forward (toward
  // camera, +z in some conventions, -z in others). MediaPipe world: +z is
  // away from camera. So right_shoulder_forward → rSh3.z < lSh3.z → yaw>0.
  const sign = lSh3.z - rSh3.z >= 0 ? 1 : -1;
  return sign * (Math.acos(cosYaw) * 180) / Math.PI;
}
// Static across calls — reasonable since we're tracking one rider in one
// session. Reset via resetBodyYawReference() when the session restarts.
const _bodyYawRef: { maxRatio: number } = { maxRatio: 0 };
export function resetBodyYawReference(): void {
  _bodyYawRef.maxRatio = 0;
}

/** Estimate head yaw in degrees from MediaPipe face landmarks. Uses the
 *  horizontal offset between nose tip (1) and the midpoint of the two
 *  outer-eye landmarks (33, 263). Approximate but sufficient for the
 *  ±20° sweep target tolerance. Returns null if landmarks are missing. */
export function estimateHeadYawDeg(
  faceLandmarks: Array<{ x: number; y: number; z?: number }> | null,
): number | null {
  if (!faceLandmarks || faceLandmarks.length < 264) return null;
  const nose = faceLandmarks[1];
  const lEye = faceLandmarks[33];
  const rEye = faceLandmarks[263];
  if (!nose || !lEye || !rEye) return null;
  const midX = (lEye.x + rEye.x) / 2;
  const eyeSpan = Math.abs(rEye.x - lEye.x);
  if (eyeSpan < 1e-4) return null;
  const offset = (nose.x - midX) / eyeSpan;
  // Map offset ∈ [-0.5, 0.5] to ~[-45°, 45°] linearly. Good enough for the
  // ±20° task — non-linearity at extremes doesn't matter inside the band.
  return offset * 90;
}
